import React, { useState } from 'react';
import { LockKeyholeIcon } from 'lucide-react';
import { PractitionerFlowShell } from '../components/practitioners/PractitionerFlowShell';
import { describeError, redirectToPractitionerConnect, signInPractitioner, startPractitionerConnectOnboarding } from '../utils/practitionerApi';
export function PractitionerPaymentSetupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      /**
       * Sign in using the practitioner's existing
       * Fem Entity account.
       *
       * signInPractitioner() also stores the temporary
       * website session so it survives the Stripe redirect.
       */
      const session = await signInPractitioner(email.trim(), password);

      /**
       * Create/reuse this practitioner's Stripe Connect
       * account and get a fresh Stripe onboarding URL.
       */
      const connect = await startPractitionerConnectOnboarding(session);

      /**
       * Leave Fem Entity and open Stripe's secure
       * hosted onboarding.
       */
      redirectToPractitionerConnect(connect.onboardingUrl);
    } catch (err) {
      setError(describeError(err));
      setSubmitting(false);
    }
  }
  return <PractitionerFlowShell currentStep={0} data-id="element-912" data-mp="26ge4wzdjwvwz">
      <div className="max-w-xl mx-auto px-6" data-id="element-913" data-mp="26hpan4qk4dqq">
        <div className="text-center" data-id="element-914" data-mp="2bfhd2sotvi96">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-purple-100" data-id="element-915" data-mp="2beformiswz9i">
            <LockKeyholeIcon aria-hidden="true" className="h-5 w-5 text-purple-700" data-id="element-916" data-mp="2gflmxzv8deqf" />
          </div>

          <h1 className="mt-6 font-serif text-4xl md:text-5xl text-charcoal leading-[1.1] tracking-tight" data-id="element-917" data-mp="2kdszsjmkpvwc">
            Set up client payments
          </h1>

          <p className="mt-5 text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-918" data-mp="gdlztqy1n38y">
            Sign in with your existing Fem Entity
            practitioner account to connect your Stripe
            account and receive client payments.
          </p>
        </div>

        <div className="mt-10 rounded-3xl border border-purple-200 bg-white px-6 py-8 md:px-9 md:py-9 shadow-[0_18px_50px_-30px_rgba(44,44,44,0.5)]" data-id="element-919" data-mp="141n6eubnw9lq">
          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-920" data-mp="z2k95w1xb7ob">
            <div data-id="element-921" data-mp="1d1sbgxtduvwq">
              <label htmlFor="practitioner-email" className="block text-sm font-semibold text-charcoal" data-id="element-922" data-mp="1d08uf4zwkgtw">
                Email
              </label>

              <input id="practitioner-email" type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} disabled={submitting} className="mt-2 w-full rounded-xl border border-charcoal/20 bg-white px-4 py-3 text-charcoal outline-none transition focus:border-purple-600 focus:ring-2 focus:ring-purple-200 disabled:opacity-60" data-id="element-923" data-mp="3ew13sh9koa2z" />
            </div>

            <div data-id="element-924" data-mp="2gznjihuz3383">
              <label htmlFor="practitioner-password" className="block text-sm font-semibold text-charcoal" data-id="element-925" data-mp="2gz46nfk6j50x">
                Password
              </label>

              <input id="practitioner-password" type="password" autoComplete="current-password" required value={password} onChange={event => setPassword(event.target.value)} disabled={submitting} className="mt-2 w-full rounded-xl border border-charcoal/20 bg-white px-4 py-3 text-charcoal outline-none transition focus:border-purple-600 focus:ring-2 focus:ring-purple-200 disabled:opacity-60" data-id="element-926" data-mp="281tt1njw33su" />
            </div>

            {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-id="element-927" data-mp="1taud33u4vqrb">
                {error}
              </p>}

            <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center rounded-full bg-purple-700 px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2" data-id="element-928" data-mp="1jkix2ix6dr42">
              {submitting ? 'Opening Stripe…' : 'Sign in & set up payments'}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-charcoal/60 leading-relaxed" data-id="element-929" data-mp="3h8lcso1guz3m">
            Your business, identity, and banking
            information will be entered securely on
            Stripe. Fem Entity does not collect your
            bank account information on this website.
          </p>
        </div>
      </div>
    </PractitionerFlowShell>;
}